<script setup lang="ts">
import { inject, onBeforeUnmount, watch, watchEffect, shallowRef } from 'vue';
import vtkCutter from '@kitware/vtk.js/Filters/Core/Cutter';
import vtkPlane from '@kitware/vtk.js/Common/DataModel/Plane';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useViewSliceStore } from '@/src/store/view-configs/slicing';
import type { LPSAxis } from '@/src/types/lps';
import { useDentalMeshStore } from './meshStore';
import { useDentalRegistrationStore } from './registrationStore';
import { transformPoint } from './rigid';
import { observeContour } from './contourObservation';
const props=defineProps<{ viewId:string;imageId?:string|null;axis:LPSAxis }>();
const view=inject(VtkViewContext)!; const mesh=useDentalMeshStore();const reg=useDentalRegistrationStore();
const transformed=shallowRef<vtkPolyData>(); const plane=vtkPlane.newInstance();const cutter=vtkCutter.newInstance();
const mapper=vtkMapper.newInstance();const actor=vtkActor.newInstance();actor.setMapper(mapper);actor.getProperty().setColor(0,1,.8);actor.getProperty().setLineWidth(2);
view.renderer.addActor(actor);cutter.setCutFunction(plane);
watch([()=>mesh.mesh,()=>mesh.matrix],()=>{
  const old=transformed.value;
  if(!mesh.mesh){transformed.value=undefined;old?.delete();return;}
  const native=mesh.mesh.getPoints().getData();const points=new Float32Array(native.length);
  for(let id=0;id<native.length/3;id++)points.set(transformPoint(mesh.matrix,native.slice(id*3,id*3+3)),id*3);
  const poly=vtkPolyData.newInstance();poly.getPoints().setData(points,3);poly.getPolys().setData(mesh.mesh.getPolys().getData());
  transformed.value=poly;old?.delete();
},{immediate:true});
watchEffect(()=>{
  const image=useImageCacheStore().getVtkImageData(props.imageId);const metadata=useImageCacheStore().getImageMetadata(props.imageId);
  if(!image || !metadata || !transformed.value || !mesh.renderInVolume || !reg.showContours){actor.setVisibility(false);view.requestRender();return;}
  const axis=metadata.lpsOrientation[props.axis];const ijk:[number,number,number]=[0,0,0];ijk[axis]=useViewSliceStore().getConfig(props.viewId,props.imageId).slice;
  const origin=image.indexToWorld(ijk);ijk[axis]+=1;const along=image.indexToWorld(ijk);const normal=along.map((n,i)=>n-origin[i]);
  plane.setOrigin(origin[0],origin[1],origin[2]);plane.setNormal(normal[0],normal[1],normal[2]);cutter.setInputData(transformed.value);cutter.modified();
  mapper.setInputData(cutter.getOutputData());actor.setVisibility(true);view.requestRender();
});
const dispose=observeContour(props.viewId,()=>({axis:props.axis,visible:actor.getVisibility(),
  points:actor.getVisibility() ? Array.from(cutter.getOutputData().getPoints().getData()) : [],origin:[...plane.getOrigin()],normal:[...plane.getNormal()]}));
onBeforeUnmount(()=>{dispose();view.renderer.removeActor(actor);mapper.delete();actor.delete();cutter.delete();plane.delete();transformed.value?.delete();});
</script>
<template><slot /></template>
